import React, { useEffect, useLayoutEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { X, ChevronRight, ChevronLeft, FileText, Target, Video, Mic, Trophy, Sparkles } from "lucide-react";
import { useEntitlement } from "@/lib/entitlement-context";

/**
 * OnboardingTour - a 5-step first-run walkthrough that introduces the
 * simplified Journey to Interview Ready. Renders as a portal on top of the
 * app shell with a soft dim overlay + spotlight ring around the target
 * element. Completes in ~30 seconds; users can Skip at any time. Once
 * completed OR skipped it never shows again for that browser (localStorage).
 */

const STORAGE_KEY = "intuiterview.onboarding.v1";
const PATH_ALLOW = "/app/dashboard";

const STEPS = [
  {
    key: "welcome",
    title: "Welcome to Intuiterview 👋",
    body: "Your journey to interview ready in five clear steps. Takes about 30 seconds.",
    icon: Sparkles,
    // No target - centered card.
    target: null,
  },
  {
    key: "application",
    title: "1. Stand Out",
    body: "Start here. Analyze how well your resume fits the job, improve your resume, and create your cover letter.",
    icon: FileText,
    target: '[data-testid="nav-resume"]',
  },
  {
    key: "predict",
    title: "2. Predict & Prepare",
    body: "Discover the questions you're most likely to be asked, then draft and refine your answers with AI help.",
    icon: Target,
    target: '[data-testid="nav-prep"]',
  },
  {
    key: "practice",
    title: "3. Practice",
    body: "Practice before it counts. Rehearse your prepared answers with the Mock Interview and AI coaching, as many times as you need.",
    icon: Video,
    target: '[data-testid="nav-practice"]',
  },
  {
    key: "official",
    title: "4. Official AI Interview",
    body: "Your official assessment, different from practice. This is where your Official Readiness Score comes from.",
    icon: Mic,
    target: '[data-testid="nav-live"]',
  },
  {
    key: "briefs",
    title: "5. Interview Briefs",
    body: "Your personalized final briefing before the real interview. The sidebar tracks all five steps of your journey.",
    icon: Trophy,
    target: '[data-testid="nav-briefs"]',
  },
];

// The tour has 6 tooltips but only 5 numbered "milestones" - the welcome step is un-numbered.
const TOTAL_MILESTONES = STEPS.length - 1;

function getStoredState() {
  try {
    return localStorage.getItem(STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

function markComplete(status) {
  try {
    localStorage.setItem(STORAGE_KEY, status);
  } catch {
    /* ignore */
  }
}

export default function OnboardingTour() {
  const { pathname } = useLocation();
  const { track } = useEntitlement();
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  // Kick off only on the dashboard, only once, only when nothing stored.
  useEffect(() => {
    if (getStoredState()) return;
    if (pathname !== PATH_ALLOW) return;
    // Small delay so the dashboard has time to paint the journey card.
    const t = setTimeout(() => {
      setStepIdx(0); // Reset to first step (fix: replay must always start at step 1)
      setActive(true);
      track("onboarding.started", { total_steps: STEPS.length });
    }, 500);
    return () => clearTimeout(t);
  }, [pathname, track]);

  const step = STEPS[stepIdx];

  // Compute the target element's position on every step change + window resize.
  useLayoutEffect(() => {
    if (!active || !step?.target) {
      setTargetRect(null);
      return undefined;
    }
    const update = () => {
      const el = document.querySelector(step.target);
      if (!el) {
        setTargetRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      // Skip zero-size or off-canvas targets (e.g. the mobile sidebar translated off-screen).
      if (r.width === 0 || r.height === 0 || r.right <= 0 || r.left >= window.innerWidth || r.bottom <= 0) {
        setTargetRect(null);
        return;
      }
      setTargetRect({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
      });
      // Scroll into view if off-screen (Interview Ready Journey lives lower on the page).
      if (r.top < 0 || r.bottom > window.innerHeight) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [active, step]);

  const complete = useCallback(
    (reason) => {
      markComplete(reason);
      setActive(false);
      track(reason === "skipped" ? "onboarding.skipped" : "onboarding.completed", {
        at_step: stepIdx,
        step_key: STEPS[stepIdx]?.key,
      });
    },
    [stepIdx, track],
  );

  const advance = useCallback(() => {
    if (stepIdx >= STEPS.length - 1) {
      complete("completed");
      return;
    }
    const next = stepIdx + 1;
    setStepIdx(next);
    track("onboarding.step_viewed", { step_index: next, step_key: STEPS[next].key });
  }, [stepIdx, complete, track]);

  const back = useCallback(() => {
    setStepIdx((i) => Math.max(0, i - 1));
  }, []);

  // Escape key skips the tour.
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") complete("skipped");
      if (e.key === "ArrowRight" || e.key === "Enter") advance();
      if (e.key === "ArrowLeft") back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, advance, back, complete]);

  const tooltipPos = useMemo(() => {
    const CARD_W = 340;
    const CARD_H = 280;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Centered card when no target, or on small screens where the 340px
    // card cannot fit beside/below anything without overflowing the viewport.
    if (!targetRect || vw < 640) {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }
    const clampLeft = (l) => `${Math.min(Math.max(8, l), vw - CARD_W - 8)}px`;
    const clampTop = (t) => `${Math.min(Math.max(8, t), vh - CARD_H - 8)}px`;
    const right = targetRect.left + targetRect.width;
    // Prefer positioning the card to the RIGHT of the sidebar element.
    // Fall back to BELOW for elements in the main content area (flipping
    // ABOVE the target when there is no room underneath).
    const isSidebar = targetRect.left < 260 && right + 16 + CARD_W <= vw;
    if (isSidebar) {
      return {
        top: clampTop(targetRect.top + targetRect.height / 2 - CARD_H / 2),
        left: clampLeft(right + 16),
        transform: "none",
      };
    }
    const below = targetRect.top + targetRect.height + 12;
    const top = below + CARD_H <= vh - 8 ? below : targetRect.top - CARD_H - 12;
    return {
      top: clampTop(top),
      left: clampLeft(targetRect.left + targetRect.width / 2 - CARD_W / 2),
      transform: "none",
    };
  }, [targetRect]);

  if (!active) return null;

  const isFirst = stepIdx === 0;
  const isLast = stepIdx === STEPS.length - 1;
  const StepIcon = step.icon;

  // Milestone label - welcome step shows 0/5 as "Intro".
  const milestoneLabel =
    stepIdx === 0
      ? "Intro"
      : `Step ${stepIdx} of ${TOTAL_MILESTONES}`;

  const overlay = (
    <div
      data-testid="onboarding-tour"
      className="fixed inset-0 z-[100] pointer-events-none"
      aria-live="polite"
      role="dialog"
      aria-label="Intuiterview onboarding tour"
    >
      {/* Dim backdrop, allows clicks through */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />

      {/* Spotlight ring around the target */}
      {targetRect && (
        <div
          data-testid="onboarding-spotlight"
          className="absolute pointer-events-none rounded-lg"
          style={{
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            boxShadow:
              "0 0 0 3px rgba(251,191,36,0.85), 0 0 0 9999px rgba(0,0,0,0.55), 0 0 32px 8px rgba(251,191,36,0.3)",
            transition: "top 250ms ease, left 250ms ease, width 250ms ease, height 250ms ease",
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        data-testid="onboarding-card"
        className="absolute pointer-events-auto w-[340px] max-w-[90vw] rounded-2xl border border-amber-500/30 bg-zinc-950/95 backdrop-blur-md shadow-[0_20px_60px_-10px_rgba(0,0,0,0.8)] p-5"
        style={tooltipPos}
      >
        {/* Header row */}
        <div className="flex items-center justify-between mb-3">
          <div className="inline-flex items-center gap-2">
            <span className="h-8 w-8 rounded-lg border border-amber-500/40 bg-amber-500/[0.08] flex items-center justify-center">
              <StepIcon className="h-4 w-4 text-amber-300" />
            </span>
            <span className="text-[10px] uppercase tracking-[0.24em] text-amber-400 font-medium tabular-nums">
              {milestoneLabel}
            </span>
          </div>
          <button
            type="button"
            onClick={() => complete("skipped")}
            data-testid="onboarding-close"
            aria-label="Close tour"
            className="text-zinc-500 hover:text-zinc-200 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="text-base font-semibold text-zinc-50">{step.title}</div>
        <div className="mt-1.5 text-[13px] text-zinc-300 leading-relaxed">{step.body}</div>

        {/* Progress dots (skip the welcome step in the dot count) */}
        <div className="mt-4 flex items-center gap-1.5">
          {STEPS.slice(1).map((s, i) => {
            const active_ = i + 1 === stepIdx;
            const done = i + 1 < stepIdx;
            return (
              <span
                key={s.key}
                data-testid={`onboarding-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${
                  active_ ? "w-6 bg-amber-400" : done ? "w-1.5 bg-amber-500/60" : "w-1.5 bg-zinc-700"
                }`}
              />
            );
          })}
        </div>

        {/* Actions */}
        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => complete("skipped")}
            data-testid="onboarding-skip"
            className="text-xs text-zinc-500 hover:text-zinc-200 transition-colors"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                type="button"
                onClick={back}
                data-testid="onboarding-prev"
                className="inline-flex items-center gap-1 text-xs text-zinc-300 hover:text-zinc-50 border border-zinc-800 hover:border-zinc-700 rounded-md px-2.5 py-1.5 transition-colors"
              >
                <ChevronLeft className="h-3 w-3" /> Back
              </button>
            )}
            <button
              type="button"
              onClick={advance}
              data-testid={isLast ? "onboarding-finish" : "onboarding-next"}
              className="inline-flex items-center gap-1 text-xs bg-amber-400 hover:bg-amber-300 text-zinc-950 font-semibold rounded-md px-3 py-1.5 transition-colors shadow-[0_0_20px_-6px_rgba(251,191,36,0.6)]"
            >
              {isLast ? (
                <>Got it <Sparkles className="h-3 w-3" /></>
              ) : (
                <>Next <ChevronRight className="h-3 w-3" /></>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
